/**
 * MobileAccordion Component - Expandable Navigation Section
 *
 * Features:
 * - Collapsible/expandable section for nested navigation
 * - Smooth height animation
 * - Accessible keyboard navigation
 * - Active state indication
 * - Uses shared NavigationItem component for consistency
 */

import React, { useState, useEffect } from "react";
import NavigationItem from "./NavigationItem";
import { useNavigationState } from "../hooks/useNavigationState";
import { MobileAccordionProps } from "../types/navigation";
import styles from "../styles/MobileAccordion.module.css";

export default function MobileAccordion({ label, icon, items, menuOpen }: MobileAccordionProps) {
    const [isExpanded, setIsExpanded] = useState(false);

    // Use shared navigation state hook
    const { isActiveSection } = useNavigationState(items);

    // Reset accordion state when menu closes - only keep active section expanded
    useEffect(() => {
        if (menuOpen === false) {
            setIsExpanded(isActiveSection);
        }
    }, [menuOpen, isActiveSection]);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    return (
        <div className={styles.accordion}>
            {/* Accordion Header */}
            <button
                className={`${styles.accordionHeader} ${isActiveSection ? styles.active : ""}`}
                onClick={toggleExpanded}
                aria-expanded={isExpanded}
                aria-label={`${label} section, ${isExpanded ? "expanded" : "collapsed"}`}
            >
                <span className={styles.headerContent}>
                    <span className={styles.icon}>{icon}</span>
                    <span className={styles.label}>{label}</span>
                </span>
                <span className={`${styles.arrow} ${isExpanded ? styles.expanded : ""}`}>▾</span>
            </button>

            {/* Accordion Content */}
            <div
                className={`${styles.accordionContent} ${isExpanded ? styles.expanded : ""}`}
                aria-hidden={!isExpanded}
            >
                <div className={styles.contentInner}>
                    {items.map((item) => (
                        <NavigationItem
                            key={item.to}
                            to={item.to}
                            label={item.label}
                            icon="▸"
                            variant="accordion"
                            end={item.to === "/Learn" || item.to === "/"}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}
